import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Input,
  Button,
  SimpleGrid,
  Icon,
  HStack,
  Badge,
  OrderedList,
  ListItem,
  AspectRatio,
  Avatar,
  Divider,
  FormControl,
  FormLabel,
  Flex,
  Image,
  Grid,
  GridItem,
  keyframes,
  Switch,
  Tooltip,
} from '@chakra-ui/react';
import {
  FaArrowRight, 
  FaStar, 
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaPlay,
  FaRocket,
  FaChartBar,
  FaTrophy,
  FaQuestionCircle,
  FaCheck,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaBuilding,
  FaSearch,
  FaLock,
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';

import AnimatedBox from './AnimatedBox';
import LandingCompetitorMap from './LandingCompetitorMap';
import LandingDashboardShowcase from './LandingDashboardShowcase';
import DataGlobe from './DataGlobe';
import LandingHeader from './LandingHeader';
import ProspectGmbSignupForm from './ProspectGmbSignupForm';

import { submitLandingForm } from '../services/landingService';
import { createLandingCheckoutSession } from '../services/billingService';
import { getRateLimitService } from '../services/rateLimitService';

// Floating animation keyframes
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const floatReverse = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(20px) rotate(-5deg); }
`;

const fadeInStep = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 102, 255, 0.15); }
  50% { box-shadow: 0 0 40px rgba(0, 102, 255, 0.35); }
`;

const orbitSpin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const confettiBurst = keyframes`
  0% { transform: translate(0, 0) scale(0); opacity: 1; }
  20% { transform: translate(var(--cx), var(--cy)) scale(1.2); opacity: 1; }
  100% { transform: translate(var(--cx), calc(var(--cy) + 60px)) scale(0.3); opacity: 0; }
`;

const scaleIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.08); }
  100% { transform: scale(1); opacity: 1; }
`;

const goldShimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const goldPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 18px rgba(255, 215, 0, 0.45), 0 4px 14px rgba(184, 134, 11, 0.35);
  }
  50% {
    box-shadow: 0 0 55px rgba(255, 215, 0, 0.9), 0 8px 32px rgba(184, 134, 11, 0.65), 0 0 0 3px rgba(255, 215, 0, 0.25);
  }
`;

const silverOutlineGlow = keyframes`
  0% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2); }
  25% { box-shadow: 0 0 0 3px rgba(215, 215, 215, 1), 0 0 18px rgba(192, 192, 192, 0.7), 0 8px 32px rgba(0, 0, 0, 0.2); }
  50% { box-shadow: 0 0 0 5px rgba(230, 230, 230, 1), 0 0 28px rgba(180, 180, 180, 0.85), 0 8px 32px rgba(0, 0, 0, 0.2); }
  75% { box-shadow: 0 0 0 3px rgba(215, 215, 215, 1), 0 0 18px rgba(192, 192, 192, 0.7), 0 8px 32px rgba(0, 0, 0, 0.2); }
  100% { box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2); }
`;

// Testimonials marquee: track holds two copies of the list, so shifting from
// -50% back to 0 scrolls the cards left-to-right and loops seamlessly.
const marqueeRight = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const CONFETTI_PIECES = Array.from({ length: 24 }, (_, i) => {
  const angle = (i / 24) * 360;
  const distance = 60 + Math.random() * 70;
  const rad = (angle * Math.PI) / 180;
  return {
    cx: Math.cos(rad) * distance,
    cy: Math.sin(rad) * distance - 30,
    color: ['#0066FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'][i % 8],
    size: 4 + Math.random() * 5,
    delay: Math.random() * 0.3,
    shape: i % 3,
  };
});

// Toggle for the testimonials/customer stories section on the landing page.
const SHOW_TESTIMONIALS = true;

const CASE_STUDIES = [
  {
    name: "Walter",
    role: "Owner",
    company: "Junkdrop",
    date: "03-12-2025",
    quote:
      "Localytics showed me exactly which neighborhoods the other haulers were winning. We doubled down on the gaps and our booked jobs took off.",
    avatar: "/testimonials/walter-junkdrop.jpg",
    logo: "",
  },
  {
    name: "Zach",
    role: "Co-Founder",
    company: "Mailbox Bros",
    date: "04-02-2025",
    quote:
      "I check Localytics before every week. Knowing what other crews charge and where their reviews come from completely changed how we market.",
    avatar: "/testimonials/zach-mailbox-bros.jpg",
    logo: "",
  },
  {
    name: "Nathan Stone",
    role: "CEO",
    company: "Supreme Plumbing",
    date: "10-06-2025",
    quote:
      "I was getting killed by the franchise down the street. Localytics showed me they were destroying me in 3 neighborhoods I thought I owned. Revenue up $47K this year just from that. This thing paid for itself in week one.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/6913a0bdd825396eb95246b0.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/690925cfe5ed230d32963158.png",
  },
  {
    name: "James Garrido",
    role: "Director of Ops",
    company: "Strategy Hospitality",
    date: "01-11-2024",
    quote:
      "We use Localytics in our monthly team meetings with staff... Show them what competitor bars are doing well vs what we are and how customer sentiment has changed.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/6913a0bd9e0b1818ddd9c0ee.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/69137ab696d06a8d31791eef.png",
  },
  {
    name: "Brock Dugger",
    role: "Founder",
    company: "Busy Bee",
    date: "12-06-2024",
    quote:
      "We needed to know what market share we had in our niche, hyper locally, and how to take more. Y'all gave us that... Team's addicted to checking it.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/6913a0bd3851a1931efe5a9d.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/690926507b01ae40a353e46f.png",
  },
  {
    name: "Jack Carr",
    role: "CEO",
    company: "Rapid Response",
    date: "02-06-2025",
    quote:
      "Good internal tools exist. Tracking local competitors was not possible before we started using this thing. We literally find out what competitors suck at and then beat them, so cool.",
    avatar:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/69138d114dedcf1b0ca9ed67.png",
    logo:
      "https://storage.googleapis.com/msgsndr/QGPyKOzkQsEY8D8pCfT8/media/690924a37b01ae20db539efd.png",
  },
];


const SCREENSHOT_GALLERY = [
  {
    image: "https://assets.cdn.filesafe.space/QGPyKOzkQsEY8D8pCfT8/media/695d8d7189c99d00001bf8b6.png",
    title: "Market Intelligence Dashboard",
    description: "Analyze demand, seasonality, and market momentum fast."
  },
  {
    image: "https://assets.cdn.filesafe.space/QGPyKOzkQsEY8D8pCfT8/media/695d8a57a88e87258ec9f1d6.png",
    title: "Business Insights Cards",
    description: "Compare competitors at a glance with live metrics."
  },
  {
    image: "https://assets.cdn.filesafe.space/QGPyKOzkQsEY8D8pCfT8/media/695d8dba3a532dc6ff578890.png",
    title: "Review Growth",
    description: "Track which competitors are accelerating before you lose ground."
  },
  {
    image: "https://assets.cdn.filesafe.space/QGPyKOzkQsEY8D8pCfT8/media/695d8ea3b75f6f21e64eed57.png",
    title: "Playbook Actions",
    description: "Action checklists that turn data into wins."
  }
];

const PRICING_PLANS = [
  {
    name: "Full Access",
    monthlyPrice: 175,
    annualPrice: 17.5,
    tagline: "Everything you need to outrank local competitors",
    features: [
      { text: "Main Dashboard (reviews, traffic, rankings)", tooltip: null },
      { text: "Business Insights + competitor breakdowns", tooltip: null },
      { text: "Pricing intelligence + market share", tooltip: null },
      { text: "Review totals, growth, and sentiment", tooltip: null },
      { text: "Localytics Score + SEO compare", tooltip: null },
      { text: "Website traffic + keyword insights", tooltip: null },
      { text: "AI SWOT reports + what's changing", tooltip: null },
      { text: "Goals, action checklist, and playbook", tooltip: null },
      { text: "Competitor ads + AI content templates", tooltip: null },
      { text: "Revenue predictions + seasonality", tooltip: null },
    ],
    cta: "Get My Dashboard",
    ctaVariant: "solid",
    isPopular: true,
  },
];

const INCLUDED_IN_ALL = [
  "See what your top 20 competitors are doing",
  "See ads, growth, rankings, reviews, and customer sentiment",
  "Live before your next team meeting",
  "We scan your market automatically",
  "Cancel anytime",
  "No contracts",
  "Full access to every feature",
];

const LOCALYTICS_ADVANTAGES = [
  {
    title: "What Semrush and Ahrefs can't do",
    description: "They track keywords. We track your actual local competitors \u2014 reviews, pricing, ads, growth, and sentiment."
  },
  {
    title: "What Birdeye misses",
    description: "Birdeye manages your reviews. Localytics shows you what every competitor is doing \u2014 and how to beat them."
  },
  {
    title: "Built for owners, not SEO agencies",
    description: "No certifications needed. Overnight competitive intelligence with zero setup headaches."
  },
  {
    title: "Revenue predictions they don't have",
    description: "Demand scores, market seasonality, and revenue forecasting \u2014 unique to Localytics."
  },
  {
    title: "Not just data \u2014 the playbook",
    description: "Goals, action checklists, and AI content templates \u2014 no other tool gives you the next steps."
  },
  {
    title: "Proven ROI for local operators",
    description: "Operators report real wins like +$47K revenue from competitive insights. Beat, don't just track."
  }
];

const TOGGLE_COMPARISON_DATA = {
  generic: {
    headline: "Your analytics stack is a liability",
    items: [
      {
        title: "Data scattered everywhere",
        description: "Juggling Semrush, Google Analytics, and review platforms. Nothing talks to each other."
      },
      {
        title: "No local competitor context",
        description: "Generic SEO tools track keywords, not the 20 businesses actually stealing your customers."
      },
      {
        title: "Insights require an expert",
        description: "Built for agencies and SEO pros. Business owners waste hours deciphering dashboards."
      },
      {
        title: "No action plan",
        description: "Lots of charts, zero guidance on what to actually do next to win more customers."
      },
      {
        title: "Expensive tool stack",
        description: "Semrush + Ahrefs + Birdeye = $500+/mo for a fraction of the competitive picture."
      }
    ]
  },
  localytics: {
    headline: "Your analytics stack is an unfair advantage",
    items: [
      {
        title: "One dashboard. Full picture.",
        description: "Reviews, traffic, ads, keywords, pricing, and sentiment — all your competitors in one view."
      },
      {
        title: "Your actual top 20 competitors",
        description: "Auto-scanned and tracked. See exactly who's winning in your local market and why."
      },
      {
        title: "Built for business owners",
        description: "Plain-English insights. No certifications, no week-long onboarding."
      },
      {
        title: "Playbook with next steps",
        description: "Goals, action checklists, and AI content templates — know exactly what to do next."
      },
      {
        title: "$175/mo. Everything included.",
        description: "Competitor tracking, SWOT analysis, revenue predictions, keyword intel, and more. No add-ons."
      }
    ]
  }
};

const FAQ_ITEMS = [
  {
    question: "What is Localytics?",
    answer: "Localytics is an AI-powered competitive intelligence platform that helps local businesses track and analyze their competitors. You can monitor competitor reviews, website traffic, ads, SEO rankings, and get actionable insights to dominate your local market. We automatically scan your market, identify your top 20 competitors, and give you a complete dashboard in about 2.5 hours."
  },
  {
    question: "How much does Localytics cost?",
    answer: "Localytics costs $175 per month with a special intro offer of $17.50 for your first month (90% off). You get full access to all features including competitive tracking, AI SWOT analysis, review monitoring, keyword research, and action playbooks. Cancel anytime, no contracts, no hidden fees."
  },
  {
    question: "How quickly can I get started?",
    answer: "Your dashboard is ready in about 2.5 hours. We automatically scan your local market and identify your top 20 competitors. No manual setup required. Just enter your business information, and we'll handle the rest. You'll receive your dashboard link by email and can start tracking competitors as soon as your data is ready."
  },
  {
    question: "What features does Localytics include?",
    answer: "Localytics includes: AI-powered SWOT analysis, competitor review tracking, website traffic analysis, competitor ads intelligence, SEO and keyword research, demand scores, seasonality insights, revenue predictions, pricing intelligence, Localytics Score (competitive ranking), and actionable playbooks with specific recommendations. Every feature is included in your subscription."
  },
  {
    question: "How is Localytics different from Semrush or Ahrefs?",
    answer: "Semrush and Ahrefs focus on SEO and keyword tracking for agencies. Localytics is built specifically for local business owners and tracks your actual local competitors — their reviews, pricing, ads, growth momentum, and customer sentiment. We provide actionable playbooks, revenue predictions, and market intelligence that SEO tools don't offer. Plus, we're ready in about 2.5 hours with zero technical setup."
  },
  {
    question: "What types of businesses use Localytics?",
    answer: "Service-based local businesses like plumbers, HVAC companies, restaurants, bars, home service providers, dentists, law firms, and other local operators who compete for customers in specific geographic markets. If you have local competitors and want to understand what they're doing to win customers, Localytics is for you."
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes, absolutely. There are no contracts, no commitments, and no cancellation fees. You can cancel your subscription anytime from your account settings. If you cancel, you'll retain access until the end of your current billing period."
  },
  {
    question: "How does the competitive tracking work?",
    answer: "We automatically scan your local market and identify your top 20 competitors based on factors like review volume, website traffic, search rankings, and market presence. We then continuously monitor their Google reviews, website performance, ads, pricing, and customer sentiment. You get real-time updates and alerts when competitors make significant changes."
  },
  {
    question: "What kind of results can I expect?",
    answer: "Our customers report significant business impact. For example, Nathan Stone (Supreme Plumbing) increased revenue by $47K after discovering competitors were dominating neighborhoods he thought he owned. James Garrido (Strategy Hospitality) uses Localytics in monthly team meetings to track competitor performance and adjust strategy. Results vary, but most operators find competitive insights that directly impact their growth strategy within the first week."
  },
  {
    question: "Do you offer a free trial?",
    answer: "We offer a special intro rate of $17.50 for your first month (90% off the regular $175/month price). This gives you full access to try all features risk-free. If you're not satisfied, you can cancel before your next billing cycle. We want you to experience the full power of competitive intelligence before committing to the regular price."
  }
];

const rateLimitService = getRateLimitService();

// Floating decoration component
const FloatingElement = ({ children, animation, delay, size, position, opacity = 0.6 }) => (
  <Box
    position="absolute"
    {...position}
    animation={`${animation} ${6 + delay}s ease-in-out infinite`}
    animationDelay={`${delay}s`}
    opacity={opacity}
    pointerEvents="none"
    fontSize={size}
    zIndex={0}
    display={{ base: "none", md: "block" }}
  >
    {children}
  </Box>
);


// Scrolls to the hero signup form
let _scrollToSignupRef = null;
const scrollToSignup = () => {
  if (_scrollToSignupRef) _scrollToSignupRef();
};

const scrollToVideo = () => {
  document.getElementById('demo-video')?.scrollIntoView({ behavior: 'smooth' });
};

const ConsumerLanding = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkoutFailed, setCheckoutFailed] = useState(false);
  const [reportRequested, setReportRequested] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedMarket, setSubmittedMarket] = useState('');
  const [submittedBusiness, setSubmittedBusiness] = useState('');
  const [isAgencyOwner] = useState(false);
  const [isIntroPricing, setIsIntroPricing] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const signupFormRef = useRef(null);
  const [comparisonView, setComparisonView] = useState('generic');
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCta(window.scrollY > 600);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSignupForm = useCallback(() => {
    signupFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  _scrollToSignupRef = scrollToSignupForm;

  const handleProspectSubmit = async ({
    email,
    anchorBusiness,
    marketKeyword,
  }) => {
    if (rateLimitService.isRateLimited('landingSubmit')) {
      setError('Please wait a moment before trying again');
      return;
    }

    const businessName = anchorBusiness?.name || 'Prospect';
    const nichePayload = (marketKeyword || '').slice(0, 100);
    const prospectUrl = anchorBusiness?.maps_url || anchorBusiness?.website || '';

    setIsLoading(true);
    setError('');
    setCheckoutFailed(false);
    setReportRequested(false);
    setSubmittedEmail(email);
    setSubmittedMarket(nichePayload);
    setSubmittedBusiness(businessName);

    try {
      await submitLandingForm(email, nichePayload, businessName, undefined, { prospectUrl });
    } catch (submitError) {
      setError(submitError.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
      return;
    }

    localStorage.setItem('emailForSignIn', email);
    localStorage.setItem('highlightAfterSignup', 'true');

    // The PDF report is free at every entry point — the lead submit above
    // queues it. The subscription upgrade is offered as an optional next step
    // below the form, never as a gate in front of the report.
    setReportRequested(true);
    setIsLoading(false);
  };

  // Optional upsell on top of the free report — the PDF is already queued by
  // the time this can run, so a failure here never costs the visitor a report.
  const startUpgradeCheckout = async () => {
    if (!submittedEmail || !submittedMarket) return;

    setIsLoading(true);
    setError('');
    try {
      const origin = window.location.origin;
      const successUrl = `${origin}/login?billing=success&email=${encodeURIComponent(submittedEmail)}`;
      const cancelUrl = `${origin}/?billing=canceled`;

      const { checkout_url } = await createLandingCheckoutSession(
        submittedEmail,
        successUrl,
        cancelUrl,
        {
          name: submittedBusiness || 'Prospect',
          niche: submittedMarket,
          isAgencyOwner,
        },
      );
      window.location.href = checkout_url;
    } catch (_checkoutError) {
      setCheckoutFailed(true);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        {/* Force the full desktop layout on mobile (scaled to fit) instead of
            the responsive mobile layout, which was squeezing sections. Matches
            the fixed-width viewport pattern used by index.html / MarketReportLanding. */}
        <meta name="viewport" content="width=1280, user-scalable=yes" />
        <title>Competitive Intelligence for Local Business | Localytics</title>
        <meta
          name="description"
          content="Competitive intelligence for local business owners. Track your top 20 local competitors' reviews, traffic, ads, and rankings — overnight, no SEO certifications required."
        />
        <meta name="keywords" content="competitive intelligence, local business analytics, competitor tracking, local SEO, review monitoring, market intelligence" />
        <link rel="canonical" href="https://app.localytics.co/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Localytics" />
        <meta property="og:title" content="Competitive Intelligence for Local Business | Localytics" />
        <meta
          property="og:description"
          content="Track every local competitor's reviews, traffic, ads, and rankings. Live dashboard before your next team meeting."
        />
        <meta property="og:image" content="https://app.localytics.co/output.png" />
        <meta property="og:image:alt" content="Localytics competitive intelligence dashboard" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://app.localytics.co/'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Competitive Intelligence for Local Business | Localytics" />
        <meta
          name="twitter:description"
          content="Track every local competitor's reviews, traffic, ads, and rankings. Live before your next team meeting."
        />
        <meta name="twitter:image" content="https://app.localytics.co/output.png" />
        
        {/* Structured Data - Product */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Localytics",
            "description": "AI-powered competitive intelligence platform for local businesses. Track competitors' reviews, website traffic, ads, and SEO performance.",
            "brand": {
              "@type": "Brand",
              "name": "Localytics"
            },
            "offers": {
              "@type": "Offer",
              "price": "175",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "url": "https://localytics.co"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "127",
              "bestRating": "5"
            }
          })}
        </script>
        
        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQ_ITEMS.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
        
        {/* Structured Data - Reviews */}
        {SHOW_TESTIMONIALS && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": CASE_STUDIES.map((testimonial, index) => ({
              "@type": "Review",
              "position": index + 1,
              "author": {
                "@type": "Person",
                "name": testimonial.name,
                "jobTitle": testimonial.role
              },
              "reviewBody": testimonial.quote,
              "reviewRating": {
                "@type": "Rating",
                "ratingValue": "5",
                "bestRating": "5"
              },
              "itemReviewed": {
                "@type": "SoftwareApplication",
                "name": "Localytics"
              }
            }))
          })}
        </script>
        )}
      </Helmet>
      
      {/* ================================================================
          MAIN CONTAINER
          ================================================================ */}
      <Box
        minH="100vh"
        bg="gray.50"
        overflowX="hidden"
        pb={{ base: showStickyCta ? '72px' : 0, md: 0 }}
      >
        

        {/* ================================================================
            SECTION 1: HERO
            Centered layout with URL input bar (Permighty-inspired)
            ================================================================ */}
        <Box
          position="relative"
          bg="linear-gradient(135deg, #0066ff 0%, #0052cc 50%, #003d99 100%)"
          overflow="hidden"
        >
          {/* Floating Decorative Elements */}
          <FloatingElement animation={float} delay={0} size="80px" position={{ top: "10%", left: "5%" }} opacity={0.3}>
            <Box bg="white" borderRadius="full" w="80px" h="80px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1} size="60px" position={{ top: "20%", right: "10%" }} opacity={0.25}>
            <Box bg="white" borderRadius="xl" w="60px" h="60px" transform="rotate(45deg)" />
          </FloatingElement>
          <FloatingElement animation={float} delay={2} size="100px" position={{ bottom: "20%", left: "8%" }} opacity={0.2}>
            <Box bg="white" borderRadius="full" w="100px" h="100px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={0.5} size="40px" position={{ bottom: "30%", right: "15%" }} opacity={0.3}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          {/* Navigation */}
          <LandingHeader onCtaClick={scrollToSignupForm} />

          {/* Hero Content - Centered */}
          <Container maxW="720px" position="relative" zIndex={10}>
            <VStack
              spacing={{ base: 3, md: 4 }}
              textAlign="center"
              align="center"
              py={{ base: 10, md: 12, lg: 14 }}
              pb={{ base: 6, md: 8 }}
            >
              <AnimatedBox animation="fadeIn" delay={0.2}>
                <Text
                  textTransform="uppercase"
                  letterSpacing="0.25em"
                  fontSize={{ base: "xs", md: "sm" }}
                  color="whiteAlpha.800"
                  fontWeight="semibold"
                >
                  Competitive Intelligence
                </Text>
              </AnimatedBox>

              <AnimatedBox animation="slideUp" delay={0.4}>
                <Heading
                  as="h1"
                  fontSize={{ base: "3xl", md: "5xl", lg: "6xl", xl: "7xl" }}
                  color="white"
                  lineHeight={1.1}
                  fontWeight="extrabold"
                  letterSpacing="tight"
                  maxW="900px"
                >
                  Which competitors{" "}
                  <Text
                    as="span"
                    bgGradient="linear(to-r, yellow.200, orange.200)"
                    bgClip="text"
                  >
                    are winning?
                  </Text>
                  <br />
                  How to beat them.
                </Heading>
              </AnimatedBox>

              <Box ref={signupFormRef} w="full">
                <AnimatedBox animation="slideUp" delay={0.6} w="full">
                  <ProspectGmbSignupForm
                    variant="hero"
                    onSubmit={handleProspectSubmit}
                    isSubmitting={isLoading}
                    error={error}
                  />
                </AnimatedBox>

                {reportRequested && !checkoutFailed && (
                  <Box
                    mt={4}
                    p={4}
                    borderRadius="xl"
                    bg="whiteAlpha.200"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    data-testid="landing-report-requested"
                  >
                    <Text color="white" fontWeight="semibold" fontSize="sm" mb={2}>
                      Your PDF report is on the way to {submittedEmail}
                    </Text>
                    <Text color="whiteAlpha.900" fontSize="sm" mb={3}>
                      {submittedBusiness} · {submittedMarket} — we&apos;re scraping the market now
                      and will email the PDF as soon as it&apos;s ready. No card required.
                    </Text>
                    <HStack spacing={3}>
                      <Button
                        size="sm"
                        bg="white"
                        color="blue.600"
                        onClick={startUpgradeCheckout}
                        isLoading={isLoading}
                        data-testid="landing-report-upgrade-cta"
                      >
                        Add the live dashboard
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        color="white"
                        borderColor="whiteAlpha.500"
                        onClick={() => setReportRequested(false)}
                      >
                        Run another market
                      </Button>
                    </HStack>
                  </Box>
                )}

                {checkoutFailed && (
                  <Box
                    mt={4}
                    p={4}
                    borderRadius="xl"
                    bg="whiteAlpha.200"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <Text color="white" fontWeight="semibold" fontSize="sm" mb={2}>
                      Your PDF report is still on the way. Checkout did not open.
                    </Text>
                    <Text color="whiteAlpha.900" fontSize="sm" mb={3}>
                      {submittedBusiness} · {submittedMarket} · {submittedEmail}
                    </Text>
                    <HStack spacing={3}>
                      <Button
                        size="sm"
                        bg="white"
                        color="blue.600"
                        onClick={startUpgradeCheckout}
                        isLoading={isLoading}
                      >
                        Try checkout again
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        color="white"
                        borderColor="whiteAlpha.500"
                        onClick={() => window.location.href = 'mailto:help@localytics.co'}
                      >
                        Contact support
                      </Button>
                    </HStack>
                  </Box>
                )}
              </Box>

            </VStack>
          </Container>

          {/* Competitor Map - embedded directly in hero gradient */}
          <Box id="competitor-map-demo" pb={{ base: 0, md: 2 }}>
            <Container maxW="90vw" position="relative" zIndex={10}>
              <VStack spacing={4}>
                <AnimatedBox animation="scale" delay={0.5} w="full">
                  <Box
                    borderRadius={{ base: "xl", md: "2xl" }}
                    overflow="hidden"
                    boxShadow="0 12px 48px rgba(0, 0, 0, 0.3)"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <LandingCompetitorMap />
                  </Box>
                </AnimatedBox>
              </VStack>
            </Container>
          </Box>

          {/* Spacer at bottom of hero gradient */}
          <Box h={{ base: 4, md: 6 }} />
        </Box>

        {/* ================================================================
            SECTION 2: SOCIAL PROOF STATS BAR
            Compact stats strip right after hero for immediate credibility
            ================================================================ */}
        <Box 
          py={{ base: 6, lg: 14 }} 
          bg="linear-gradient(135deg, #0066ff 0%, #0052cc 100%)"
          position="relative"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0} size="60px" position={{ top: "10%", left: "5%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="60px" h="60px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1} size="40px" position={{ bottom: "20%", right: "10%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          <Container maxW="90vw" position="relative" zIndex={1}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 8 }}>
              {[
                { number: "Top 20", label: "Competitors Tracked", icon: FaTrophy },
                { number: "2.5 hrs", label: "Dashboard Ready", icon: FaRocket },
                { number: "Full Access", label: "Ads, Growth, Rankings", icon: FaChartBar },
                { number: "No Contracts", label: "Cancel Anytime", icon: FaCheckCircle },
              ].map((stat, index) => (
                <AnimatedBox key={index} animation="scale" delay={0.1 * index}>
                  <VStack
                    spacing={{ base: 1.5, md: 3 }}
                    p={{ base: 3, md: 6 }}
                    bg="whiteAlpha.150"
                    borderRadius="2xl"
                    backdropFilter="blur(10px)"
                    h="full"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                  >
                    <Flex w={{ base: "34px", md: "52px" }} h={{ base: "34px", md: "52px" }} justify="center" align="center" bg="whiteAlpha.200" borderRadius="full" flexShrink={0}>
                      <Icon as={stat.icon} boxSize={{ base: 4, md: 6 }} color="white" />
                    </Flex>
                    <Text fontSize={{ base: "md", md: "3xl" }} fontWeight="extrabold" color="white" lineHeight={1}>
                      {stat.number}
                    </Text>
                    <Text color="whiteAlpha.900" fontSize={{ base: "xs", md: "md" }} fontWeight="semibold" textAlign="center" lineHeight={1.3}>
                      {stat.label}
                    </Text>
                  </VStack>
                </AnimatedBox>
              ))}
            </SimpleGrid>
          </Container>
        </Box>


        {/* ================================================================
            CUSTOMER STORIES
            Horizontal marquee — cards scroll left-to-right and loop
            seamlessly (two duplicated copies of the list). Pauses on hover.
            ================================================================ */}
        {SHOW_TESTIMONIALS && (
        <Box py={{ base: 10, lg: 14 }} bg="gray.50" overflow="hidden">
          <Container maxW="90vw" mb={8}>
            <VStack spacing={6}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.500"
                fontWeight="medium"
                textAlign="center"
                letterSpacing="wide"
              >
                See how operators use Localytics to outrank competitors.
              </Text>
            </VStack>
          </Container>

          <Box
            position="relative"
            w="full"
            overflow="hidden"
            sx={{
              maskImage:
                "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
            }}
          >
            <HStack
              spacing={{ base: 5, md: 6 }}
              w="max-content"
              align="stretch"
              animation={`${marqueeRight} 45s linear infinite`}
              _hover={{ animationPlayState: "paused" }}
            >
              {[...CASE_STUDIES, ...CASE_STUDIES].map((story, index) => (
                <Box
                  key={index}
                  flexShrink={0}
                  w={{ base: "300px", md: "420px" }}
                  p={{ base: 5, md: 7 }}
                  bg="white"
                  borderRadius="2xl"
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="gray.100"
                  transition="all 0.25s ease"
                  _hover={{ transform: "translateY(-4px)", boxShadow: "lg", borderColor: "blue.200" }}
                >
                  <VStack align="start" spacing={4} h="full">
                    <HStack spacing={4} w="full">
                      <Avatar
                        src={story.avatar}
                        name={story.name}
                        size={{ base: "md", md: "lg" }}
                        border="2px solid"
                        borderColor="gray.100"
                      />
                      <VStack align="start" spacing={0} flex="1" minW={0}>
                        <Text fontWeight="bold" color="gray.800" fontSize={{ base: "sm", md: "md" }} lineHeight="1.1">
                          {story.name}
                        </Text>
                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" lineHeight="1.1">
                          {story.role} &middot; {story.company}
                        </Text>
                      </VStack>
                      <HStack spacing={0.5} flexShrink={0}>
                        {Array.from({ length: 5 }).map((_, starIndex) => (
                          <Icon key={starIndex} as={FaStar} color="yellow.400" boxSize={{ base: 3, md: 3.5 }} />
                        ))}
                      </HStack>
                    </HStack>
                    <Text fontSize={{ base: "sm", md: "md" }} color="gray.600" lineHeight="tall" flex="1">
                      &ldquo;{story.quote}&rdquo;
                    </Text>
                    <Button
                      size="sm"
                      variant="link"
                      color="#0066FF"
                      rightIcon={<FaArrowRight />}
                      fontWeight="semibold"
                      _hover={{ color: "#0052CC", textDecoration: "none" }}
                      onClick={scrollToSignup}
                    >
                      Get started
                    </Button>
                  </VStack>
                </Box>
              ))}
            </HStack>
          </Box>
        </Box>
        )}

        {/* ================================================================
            SECTION 2.3: INTELLIGENCE GLOBE
            3D data globe showcasing all the signals Localytics tracks
            ================================================================ */}
        <Box
          py={{ base: 14, lg: 20 }}
          bg="#f4f3ef"
          position="relative"
          overflow="hidden"
        >
          <VStack spacing={0} align="center" px={4}>
            {/* Eyebrow */}
            <Flex
              align="center"
              gap={3}
              mb={6}
              sx={{
                '&::before, &::after': {
                  content: '""',
                  display: 'block',
                  width: '28px',
                  height: '1px',
                  background: '#1a6cff',
                  flexShrink: 0,
                },
              }}
            >
              <Text
                fontFamily="'DM Mono', monospace"
                fontSize="10px"
                fontWeight={400}
                color="#1a6cff"
                letterSpacing="0.2em"
                textTransform="uppercase"
              >
                Overnight Competitive Intelligence
              </Text>
            </Flex>

            {/* Heading */}
            <Text
              as="h2"
              fontFamily="'Syne', sans-serif"
              fontWeight={800}
              fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
              letterSpacing="-0.03em"
              color="#1a1a22"
              textAlign="center"
              lineHeight={1.18}
              mb={4}
            >
              Every Competitive Insight{' '}
              <Box
                as="span"
                bgGradient="linear(105deg, #1a6cff 0%, #6d3fc0 55%, #c02060 100%)"
                bgClip="text"
              >
                That Exists
              </Box>
            </Text>

            {/* Sub text */}
            <Text
              fontFamily="'DM Mono', monospace"
              fontSize={{ base: '11px', md: '12px' }}
              fontWeight={400}
              color="#8a8898"
              lineHeight={1.8}
              textAlign="center"
              maxW="600px"
              mb={8}
            >
              Reviews · Sentiment · SEO · Web Traffic · Competitor Ad Spend · Revenue · Keyword Gaps · Local Pack · AI SWOT · and more
            </Text>

            {/* Globe canvas */}
            <Box w="100%" maxW="720px">
              <DataGlobe />
            </Box>

          </VStack>
        </Box>

        {/* ================================================================
            SECTION 2.5: INTERACTIVE DASHBOARD SHOWCASE
            19 clickable question bubbles that load live dashboards
            ================================================================ */}
        <LandingDashboardShowcase />


        {/* Competitor Map section moved into hero above */}


        {/* ================================================================
            SECTION 5: HOW IT WORKS + SCREENSHOTS
            Explanation with checklist on left, screenshot gallery on right
            ================================================================ */}
        <Box py={{ base: 12, lg: 16 }} bg="white">
          <Container maxW="90vw">
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={10} alignItems="center">
              {/* Left Column - Explanation */}
              <GridItem>
                <VStack align={{ base: "center", lg: "flex-start" }} spacing={5} textAlign={{ base: "center", lg: "left" }}>
                  <AnimatedBox animation="slideUp" delay={0.3}>
                    <Heading size="xl" color="gray.800" lineHeight={1.2}>
                      How it actually works{" "}
                      <Text as="span" color="blue.500">behind the scenes</Text>
                    </Heading>
                  </AnimatedBox>
                  <AnimatedBox animation="fadeIn" delay={0.4}>
                    <Text fontSize="lg" color="gray.600">
                      Get a behind the scenes look at all 10 features and how businesses are using them to out-rank, outsell,
                      and outperform competitors.
                    </Text>
                  </AnimatedBox>
                  <AnimatedBox animation="slideUp" delay={0.5}>
                    <VStack align={{ base: "flex-start", lg: "flex-start" }} spacing={2.5} w="full">
                      {[
                        "We scan your market and pull competitors automatically",
                        "See what your top 20 competitors are doing and how to beat them",
                        "See ads, growth, rankings, reviews, and customer sentiment",
                        "Live before your next team meeting — no week-long onboarding"
                      ].map((item, index) => (
                        <HStack key={index} spacing={3} align="flex-start">
                          <Flex w={5} h={5} bg="green.100" borderRadius="full" align="center" justify="center" flexShrink={0} mt={0.5}>
                            <Icon as={FaCheckCircle} color="green.500" boxSize={3} />
                          </Flex>
                          <Text color="gray.700" fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>{item}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </AnimatedBox>
                  <AnimatedBox animation="fadeIn" delay={0.6}>
                    <Flex
                      direction={{ base: "column", sm: "row" }}
                      gap={{ base: 3, sm: 4 }}
                      pt={2}
                      align={{ base: "stretch", sm: "center" }}
                    >
                      <Button
                        size="lg"
                        bg="#0066FF"
                        color="white"
                        rightIcon={<FaArrowRight />}
                        onClick={scrollToSignup}
                        px={8}
                        fontWeight="bold"
                        _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                      >
                        Get My Dashboard
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        color="#0066FF"
                        borderColor="#0066FF"
                        borderWidth="2px"
                        px={6}
                        fontWeight="semibold"
                        onClick={scrollToVideo}
                        _hover={{ transform: "translateY(-2px)", bg: "blue.50" }}
                      >
                        Watch Explainer
                      </Button>
                    </Flex>
                  </AnimatedBox>
                </VStack>
              </GridItem>

              {/* Right Column - Screenshots */}
              <GridItem>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                  {SCREENSHOT_GALLERY.map((shot, index) => (
                    <AnimatedBox key={index} animation="slideUp" delay={0.3 + index * 0.1}>
                      <Box
                        p={4}
                        bg="gray.50"
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.100"
                        transition="all 0.3s"
                        h="full"
                        _hover={{ transform: "translateY(-4px)", boxShadow: "md", borderColor: "blue.200" }}
                      >
                        <VStack align="start" spacing={2.5}>
                          <Image
                            src={shot.image}
                            alt={`${shot.title} screenshot`}
                            borderRadius="lg"
                            w="full"
                            h={{ base: "160px", sm: "110px" }}
                            objectFit="cover"
                          />
                          <Text fontWeight="semibold" color="gray.800" fontSize="sm">{shot.title}</Text>
                          <Text color="gray.500" fontSize="xs" lineHeight="tall">{shot.description}</Text>
                        </VStack>
                      </Box>
                    </AnimatedBox>
                  ))}
                </SimpleGrid>
              </GridItem>
            </Grid>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 6: VIDEO DEMO
            Product demo video in a dark section
            ================================================================ */}
        <Box py={{ base: 12, lg: 16 }} bg="gray.900" id="demo-video">
          <Container maxW="90vw">
            <VStack spacing={8}>
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge bg="blue.500" color="white" px={3} py={1} borderRadius="full" fontSize="sm">
                    Watch Explainer
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="white">See Localytics in action</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="md" color="gray.400">
                    Analyze competitors' website traffic, Google reviews, and ads to see exactly how they win.
                  </Text>
                </AnimatedBox>
              </VStack>

              <AnimatedBox animation="scale" delay={0.5} w="full" maxW="900px">
                <Box borderRadius="2xl" overflow="hidden" boxShadow="2xl" position="relative" bg="gray.800">
                  <Box
                    id="videoOverlay"
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="blackAlpha.600"
                    zIndex="1"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    transition="all 0.3s"
                    _hover={{ bg: "blackAlpha.500" }}
                    onClick={(e) => {
                      const video = document.getElementById('demoVideo');
                      if (video) {
                        if (video.paused) {
                          video.play();
                          e.currentTarget.style.display = 'none';
                        }
                      }
                    }}
                  >
                    <Flex
                      w={{ base: 14, md: 20 }}
                      h={{ base: 14, md: 20 }}
                      bg="white"
                      borderRadius="full"
                      align="center"
                      justify="center"
                      boxShadow="0 4px 30px rgba(0,0,0,0.3)"
                      transition="all 0.3s"
                      _hover={{ transform: "scale(1.1)" }}
                    >
                      <Icon as={FaPlay} boxSize={{ base: 6, md: 8 }} color="blue.500" ml={1} />
                    </Flex>
                  </Box>
                  
                  <AspectRatio ratio={16/9}>
                    <video
                      id="demoVideo"
                      title="Localytics Platform Demo Video"
                      poster="/videos/localytics-poster.jpg"
                      preload="metadata"
                      style={{ 
                        display: "block",
                        width: "100%",
                        height: "103%",
                        objectFit: "cover",
                        objectPosition: "center top"
                      }}
                      onClick={(e) => {
                        if (!e.target.paused) {
                          e.target.pause();
                          const overlay = document.getElementById('videoOverlay');
                          if (overlay) overlay.style.display = 'flex';
                        }
                      }}
                      onPause={() => {
                        const overlay = document.getElementById('videoOverlay');
                        if (overlay) overlay.style.display = 'flex';
                      }}
                      onLoadedData={(e) => {
                        const video = e.target;
                        video.currentTime = 1.5;
                        const handleSeeked = () => {
                          video.pause();
                          video.removeEventListener('seeked', handleSeeked);
                        };
                        video.addEventListener('seeked', handleSeeked);
                      }}
                    >
                      <source src="/videos/output.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </AspectRatio>
                </Box>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 7: TOGGLE COMPARISON
            Side-by-side toggle comparing generic tools vs Localytics
            ================================================================ */}
        <Box py={{ base: 14, lg: 20 }} bg="#FDF8F3">
          <Container maxW="800px">
            <VStack spacing={{ base: 8, md: 10 }}>
              <AnimatedBox animation="slideUp" delay={0.2}>
                <Heading
                  size={{ base: "xl", md: "2xl" }}
                  color="gray.800"
                  textAlign="center"
                  lineHeight={1.15}
                  fontWeight="bold"
                  px={{ base: 2, md: 0 }}
                >
                  {comparisonView === 'generic'
                    ? TOGGLE_COMPARISON_DATA.generic.headline
                    : TOGGLE_COMPARISON_DATA.localytics.headline}
                </Heading>
              </AnimatedBox>

              <Flex
                bg="gray.100"
                borderRadius="full"
                p="4px"
                w="fit-content"
                mx="auto"
                position="relative"
              >
                <Button
                  size={{ base: "sm", md: "md" }}
                  borderRadius="full"
                  px={{ base: 5, md: 8 }}
                  fontWeight="bold"
                  fontSize={{ base: "xs", md: "sm" }}
                  letterSpacing="wide"
                  textTransform="uppercase"
                  bg={comparisonView === 'generic' ? "gray.800" : "transparent"}
                  color={comparisonView === 'generic' ? "white" : "gray.500"}
                  _hover={comparisonView === 'generic'
                    ? { bg: "gray.800" }
                    : { color: "gray.700" }
                  }
                  onClick={() => setComparisonView('generic')}
                  transition="all 0.25s ease"
                  minH="auto"
                  h={{ base: "36px", md: "44px" }}
                >
                  Generic Tools
                </Button>
                <Button
                  size={{ base: "sm", md: "md" }}
                  borderRadius="full"
                  px={{ base: 5, md: 8 }}
                  fontWeight="bold"
                  fontSize={{ base: "xs", md: "sm" }}
                  bg={comparisonView === 'localytics' ? "gray.800" : "transparent"}
                  color={comparisonView === 'localytics' ? "white" : "gray.500"}
                  _hover={comparisonView === 'localytics'
                    ? { bg: "gray.800" }
                    : { color: "gray.700" }
                  }
                  onClick={() => setComparisonView('localytics')}
                  transition="all 0.25s ease"
                  minH="auto"
                  h={{ base: "36px", md: "44px" }}
                >
                  <Text>LOCALYTICS</Text>
                </Button>
              </Flex>

              <Box
                w="full"
                bg={comparisonView === 'localytics' ? "#0066FF" : "white"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={comparisonView === 'localytics' ? "transparent" : "gray.200"}
                overflow="hidden"
                boxShadow="lg"
                transition="all 0.4s ease"
              >
                <VStack spacing={0} align="stretch">
                  {(comparisonView === 'generic'
                    ? TOGGLE_COMPARISON_DATA.generic.items
                    : TOGGLE_COMPARISON_DATA.localytics.items
                  ).map((item, index) => (
                    <HStack
                      key={`${comparisonView}-${index}`}
                      spacing={{ base: 4, md: 5 }}
                      px={{ base: 5, md: 8 }}
                      py={{ base: 5, md: 6 }}
                      borderBottomWidth={index < 4 ? "1px" : "0"}
                      borderColor={comparisonView === 'localytics'
                        ? "rgba(255,255,255,0.1)"
                        : "gray.100"
                      }
                      align="flex-start"
                      transition="all 0.3s ease"
                      sx={{
                        animation: `${fadeInStep} 0.4s ease forwards`,
                        animationDelay: `${index * 0.06}s`,
                        opacity: 0,
                      }}
                    >
                      <Flex
                        w={{ base: "36px", md: "44px" }}
                        h={{ base: "36px", md: "44px" }}
                        borderRadius="xl"
                        align="center"
                        justify="center"
                        flexShrink={0}
                        bg={comparisonView === 'localytics'
                          ? "rgba(255,255,255,0.12)"
                          : "gray.50"
                        }
                        mt={0.5}
                      >
                        <Icon
                          as={comparisonView === 'localytics' ? FaCheck : FaTimes}
                          boxSize={{ base: 3.5, md: 4 }}
                          color={comparisonView === 'localytics'
                            ? "white"
                            : "gray.400"
                          }
                        />
                      </Flex>
                      <VStack align="start" spacing={1} flex="1">
                        <Text
                          fontWeight="bold"
                          fontSize={{ base: "sm", md: "md" }}
                          color={comparisonView === 'localytics' ? "white" : "gray.800"}
                        >
                          {item.title}
                        </Text>
                        <Text
                          fontSize={{ base: "xs", md: "sm" }}
                          color={comparisonView === 'localytics'
                            ? "whiteAlpha.700"
                            : "gray.500"
                          }
                          lineHeight="tall"
                        >
                          {item.description}
                        </Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {comparisonView === 'localytics' && (
                <AnimatedBox animation="fadeIn" delay={0.3}>
                  <Button
                    size="lg"
                    bg="white"
                    color="#0066FF"
                    rightIcon={<FaArrowRight />}
                    onClick={scrollToSignup}
                    px={10}
                    fontWeight="bold"
                    borderRadius="full"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "gray.100" }}
                  >
                    Get My Dashboard
                  </Button>
                </AnimatedBox>
              )}
            </VStack>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 8: FEATURE MATRIX
            Detailed breakdown of what's inside each area
            ================================================================ */}
        <Box py={{ base: 12, lg: 16 }} bg="white">
          <Container maxW="90vw">
            <VStack spacing={10}>
              {/* Section Header */}
              <VStack spacing={3} textAlign="center" maxW="1000px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
                    Competitive Comparison
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800">Localytics vs the big names</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.600">
                    See how Localytics stacks up against Semrush, Ahrefs, and Birdeye for local business intelligence.
                  </Text>
                </AnimatedBox>
              </VStack>

              {/* Comparison table — rendered as a single image so it stays
                  perfectly formatted at every viewport (including the scaled
                  fixed-width mobile layout). */}
              <AnimatedBox animation="slideUp" delay={0.5} w="full">
                <Image
                  src="/localytics-comparison-table.png"
                  alt="Feature comparison of Localytics vs Semrush, Ahrefs, and Birdeye for local business intelligence"
                  w="full"
                  h="auto"
                  borderRadius="2xl"
                  boxShadow="lg"
                  border="1px solid"
                  borderColor="gray.200"
                />
              </AnimatedBox>

              {/* Why operators choose Localytics */}
              <AnimatedBox animation="slideUp" delay={0.6} w="full">
                <Box bg="gray.50" borderRadius="2xl" p={{ base: 5, md: 7 }} border="1px solid" borderColor="gray.200">
                  <VStack spacing={5} align="stretch">
                    <Heading size="md" color="gray.800" textAlign="center">
                      Why local operators switch to Localytics
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                      {LOCALYTICS_ADVANTAGES.map((advantage, index) => (
                        <HStack 
                          key={index} 
                          align="flex-start" 
                          spacing={3}
                          p={3}
                          bg="white"
                          borderRadius="xl"
                          transition="all 0.2s"
                          _hover={{ bg: "blue.50", transform: "translateY(-2px)" }}
                        >
                          <Flex w={5} h={5} bg="green.100" borderRadius="full" align="center" justify="center" flexShrink={0} mt={0.5}>
                            <Icon as={FaCheck} color="green.500" boxSize={2.5} />
                          </Flex>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" color="gray.800" fontSize="sm">{advantage.title}</Text>
                            <Text color="gray.600" fontSize="xs">{advantage.description}</Text>
                          </VStack>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </VStack>
                </Box>
              </AnimatedBox>

              <Text fontSize="xs" color="gray.400" textAlign="center" fontStyle="italic">
                Comparison reflects publicly available competitor information at time of viewing — last reviewed{' '}
                {new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}. Pricing reflects entry-level plans.
              </Text>
            </VStack>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 9: PRICING
            Single plan, centered, with feature list and included items
            ================================================================ */}
        <Box py={{ base: 12, lg: 16 }} bg="gray.50" id="pricing">
          <Container maxW="85vw">
            <VStack spacing={10}>
              {/* Section Header */}
              <VStack spacing={3} textAlign="center" maxW="900px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">Pricing</Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800">Launch pricing — first month $17.50</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.500">
                    Try the full platform for $17.50 your first month, then $175/month. Cancel anytime.
                  </Text>
                </AnimatedBox>

                {/* Billing Toggle */}
                <AnimatedBox animation="fadeIn" delay={0.5}>
                  <HStack spacing={{ base: 2, md: 4 }} bg="gray.100" p={2} borderRadius="full" mt={2}>
                    <Text mb={0} fontSize={{ base: "xs", md: "sm" }} fontWeight={!isIntroPricing ? "semibold" : "normal"} color={!isIntroPricing ? "gray.800" : "gray.500"}>
                      Standard
                    </Text>
                    <Switch colorScheme="blue" size="md" isChecked={isIntroPricing} onChange={() => setIsIntroPricing(!isIntroPricing)} />
                    <HStack spacing={1}>
                      <Text mb={0} fontSize={{ base: "xs", md: "sm" }} fontWeight={isIntroPricing ? "semibold" : "normal"} color={isIntroPricing ? "gray.800" : "gray.500"}>
                        First Month
                      </Text>
                      <Badge colorScheme="green" fontSize="2xs" borderRadius="full">$17.50</Badge>
                    </HStack>
                  </HStack>
                </AnimatedBox>
              </VStack>

              {/* Pricing Card - Centered single card */}
              {PRICING_PLANS.map((plan, index) => (
                <AnimatedBox key={index} animation="slideUp" delay={0.3} w="full" maxW="520px">
                  <Box
                    p={{ base: 5, md: 8 }}
                    bg="white"
                    borderRadius="2xl"
                    border="2px solid"
                    borderColor="blue.500"
                    position="relative"
                    boxShadow="xl"
                    transition="all 0.3s"
                    _hover={{ transform: "translateY(-6px)", boxShadow: "2xl" }}
                  >
                    {/* Popular Badge */}
                    <Badge
                      position="absolute"
                      top={-3}
                      left="50%"
                      transform="translateX(-50%)"
                      bg="blue.500"
                      color="white"
                      px={4}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Most Popular
                    </Badge>

                    <VStack spacing={5} align="stretch">
                      {/* Plan Header */}
                      <VStack align="center" spacing={1}>
                        <Heading size="md" color="gray.800">{plan.name}</Heading>
                        <Text fontSize="sm" color="gray.500">{plan.tagline}</Text>
                      </VStack>

                      {/* Price */}
                      <VStack spacing={0.5}>
                        <HStack align="baseline" spacing={1} justify="center">
                          <Text fontSize="5xl" fontWeight="bold" color="gray.800">
                            ${isIntroPricing ? Number(plan.annualPrice).toFixed(2) : plan.monthlyPrice}
                          </Text>
                          <Text fontSize="md" color="gray.500">/month</Text>
                        </HStack>
                        {isIntroPricing && (
                          <Text fontSize="xs" color="green.500">First month only · then ${plan.monthlyPrice}/month</Text>
                        )}
                        {!isIntroPricing && (
                          <Text fontSize="xs" color="gray.500">Full access. Cancel anytime.</Text>
                        )}
                      </VStack>

                      <Divider />

                      {/* Features */}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2.5}>
                        {plan.features.map((feature, featureIndex) => (
                          <HStack key={featureIndex} align="start" spacing={2.5}>
                            <Flex w="18px" h="18px" bg="green.100" borderRadius="full" align="center" justify="center" flexShrink={0} mt={0.5}>
                              <Icon as={FaCheck} color="green.500" boxSize={2} />
                            </Flex>
                            <HStack spacing={1} align="center">
                              <Text fontSize="sm" color="gray.600">{feature.text}</Text>
                              {feature.tooltip && (
                                <Tooltip label={feature.tooltip} placement="top" hasArrow bg="gray.700" color="white" fontSize="xs" px={3} py={2} borderRadius="md">
                                  <Box as="span" cursor="help">
                                    <Icon as={FaQuestionCircle} color="gray.400" boxSize={3} />
                                  </Box>
                                </Tooltip>
                              )}
                            </HStack>
                          </HStack>
                        ))}
                      </SimpleGrid>

                      {/* CTA Button */}
                      <Button
                        w="full"
                        size="lg"
                        colorScheme="blue"
                        borderRadius="xl"
                        fontWeight="bold"
                        py={6}
                        _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                        onClick={scrollToSignup}
                      >
                        {plan.cta}
                      </Button>
                    </VStack>
                  </Box>
                </AnimatedBox>
              ))}

              {/* What's Included */}
              <AnimatedBox animation="fadeIn" delay={0.5} w="full">
                <Box bg="white" borderRadius="2xl" p={6} textAlign="center" border="1px solid" borderColor="gray.200">
                  <VStack spacing={3}>
                    <Heading size="sm" color="gray.700">Included with every plan</Heading>
                    <Flex wrap="wrap" justify="center" gap={2} maxW="1000px">
                      {INCLUDED_IN_ALL.map((item, index) => (
                        <HStack key={index} spacing={1.5} bg="gray.50" px={3} py={1.5} borderRadius="full" border="1px solid" borderColor="gray.100">
                          <Icon as={FaCheckCircle} color="blue.500" boxSize={3} />
                          <Text mb={0} fontSize="xs" color="gray.600">{item}</Text>
                        </HStack>
                      ))}
                    </Flex>
                  </VStack>
                </Box>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 10: FAQ
            Frequently Asked Questions
            ================================================================ */}
        <Box py={{ base: 12, lg: 16 }} bg="white" id="faq">
          <Container maxW="container.lg">
            <VStack spacing={10}>
              {/* Section Header */}
              <VStack spacing={3} textAlign="center" maxW="700px">
                <AnimatedBox animation="fadeIn" delay={0.2}>
                  <Badge colorScheme="purple" px={3} py={1} borderRadius="full" fontSize="sm">
                    FAQ
                  </Badge>
                </AnimatedBox>
                <AnimatedBox animation="slideUp" delay={0.3}>
                  <Heading size="xl" color="gray.800">Frequently Asked Questions</Heading>
                </AnimatedBox>
                <AnimatedBox animation="fadeIn" delay={0.4}>
                  <Text fontSize="lg" color="gray.500">
                    Everything you need to know about Localytics and competitive intelligence for local businesses.
                  </Text>
                </AnimatedBox>
              </VStack>

              {/* FAQ Accordion */}
              <VStack spacing={3} w="full" align="stretch">
                {FAQ_ITEMS.map((faq, index) => (
                  <AnimatedBox key={index} animation="slideUp" delay={0.2 + index * 0.05}>
                    <Box
                      bg="white"
                      borderRadius="xl"
                      border="2px solid"
                      borderColor={openFaqIndex === index ? "blue.500" : "gray.200"}
                      overflow="hidden"
                      transition="all 0.3s"
                      _hover={{ borderColor: "blue.300", boxShadow: "md" }}
                    >
                      <Button
                        w="full"
                        h="auto"
                        py={5}
                        px={6}
                        bg="transparent"
                        _hover={{ bg: "gray.50" }}
                        _active={{ bg: "gray.100" }}
                        onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                        borderRadius="none"
                        textAlign="left"
                        fontWeight="normal"
                      >
                        <HStack w="full" justify="space-between" align="start" spacing={4}>
                          <VStack align="start" spacing={1} flex="1">
                            <Text fontWeight="semibold" color="gray.800" fontSize={{ base: "md", md: "lg" }} lineHeight="tall">
                              {faq.question}
                            </Text>
                          </VStack>
                          <Flex
                            w="32px"
                            h="32px"
                            bg={openFaqIndex === index ? "blue.500" : "gray.100"}
                            borderRadius="full"
                            align="center"
                            justify="center"
                            flexShrink={0}
                            transition="all 0.3s"
                          >
                            <Icon
                              as={openFaqIndex === index ? FaChevronUp : FaChevronDown}
                              color={openFaqIndex === index ? "white" : "gray.600"}
                              boxSize={4}
                            />
                          </Flex>
                        </HStack>
                      </Button>
                      <Box
                        maxH={openFaqIndex === index ? "500px" : "0"}
                        overflow="hidden"
                        transition="max-height 0.3s ease-in-out"
                      >
                        <Box px={6} pb={5} pt={0}>
                          <Text color="gray.600" fontSize={{ base: "sm", md: "md" }} lineHeight="tall">
                            {faq.answer}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  </AnimatedBox>
                ))}
              </VStack>

              {/* Still have questions CTA */}
              <AnimatedBox animation="fadeIn" delay={0.7} w="full">
                <Box
                  bg="gradient-to-r from-blue.50 to-purple.50"
                  bgGradient="linear(to-r, blue.50, purple.50)"
                  borderRadius="xl"
                  p={6}
                  textAlign="center"
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <VStack spacing={3}>
                    <Flex w={12} h={12} bg="blue.100" borderRadius="full" align="center" justify="center" mx="auto">
                      <Icon as={FaQuestionCircle} color="blue.600" boxSize={6} />
                    </Flex>
                    <Heading size="md" color="gray.800">Still have questions?</Heading>
                    <Text color="gray.600" fontSize="md" maxW="500px">
                      Get your dashboard and see exactly how Localytics works. Our platform is intuitive and designed for business owners, not technical experts.
                    </Text>
                    <Button
                      size="lg"
                      bg="#0066FF"
                      color="white"
                      rightIcon={<FaArrowRight />}
                      onClick={scrollToSignup}
                      fontWeight="bold"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "lg", bg: "#0052CC" }}
                    >
                      Get Started for $17.50
                    </Button>
                  </VStack>
                </Box>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 10.5: AGENCY WHITELABEL WIDGET
            Promotes the agency whitelabel program
            ================================================================ */}
        <Box py={{ base: 10, lg: 14 }} bg="linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)" position="relative" overflow="hidden">
          <FloatingElement animation={float} delay={0.5} size="60px" position={{ top: "15%", right: "8%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="60px" h="60px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1} size="40px" position={{ bottom: "20%", left: "6%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="40px" h="40px" />
          </FloatingElement>

          <Container maxW="90vw" position="relative" zIndex={1}>
            <AnimatedBox animation="slideUp" delay={0.2}>
              <Grid
                templateColumns={{ base: "1fr", lg: "1fr auto" }}
                gap={{ base: 6, lg: 10 }}
                alignItems="center"
                bg="whiteAlpha.100"
                borderRadius="2xl"
                p={{ base: 6, md: 8 }}
                backdropFilter="blur(10px)"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <GridItem>
                  <VStack align={{ base: "center", lg: "flex-start" }} spacing={3} textAlign={{ base: "center", lg: "left" }}>
                    <Badge bg="whiteAlpha.200" color="white" px={3} py={1} borderRadius="full" fontSize="sm" border="1px solid" borderColor="whiteAlpha.300">
                      <HStack spacing={2}>
                        <Icon as={FaBuilding} boxSize={3} />
                        <Text mb={0}>For Agencies</Text>
                      </HStack>
                    </Badge>
                    <Heading size={{ base: "lg", md: "xl" }} color="white" lineHeight={1.2}>
                      Run an agency?{" "}
                      <Text as="span" bgGradient="linear(to-r, #00d2ff, #3a7bd5)" bgClip="text">
                        Whitelabel Localytics.
                      </Text>
                    </Heading>
                    <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.800" maxW="600px">
                      Deliver competitive intelligence dashboards to your clients under your own brand.
                      Your logo, your domain, your pricing. We stay invisible.
                    </Text>
                    <HStack spacing={4} flexWrap="wrap" pt={1}>
                      {["Your brand on every screen", "Recurring revenue", "48hr setup"].map((item, i) => (
                        <HStack key={i} spacing={2}>
                          <Icon as={FaCheckCircle} color="green.300" boxSize={3.5} />
                          <Text mb={0} fontSize="sm" color="whiteAlpha.800">{item}</Text>
                        </HStack>
                      ))}
                    </HStack>
                  </VStack>
                </GridItem>
                <GridItem>
                  <VStack spacing={3}>
                    <Button
                      as={RouterLink}
                      to="/agencies"
                      size="lg"
                      bg="white"
                      color="#0f3460"
                      rightIcon={<FaArrowRight />}
                      px={8}
                      fontWeight="bold"
                      _hover={{ transform: "translateY(-3px)", boxShadow: "0 10px 40px rgba(0,0,0,0.3)", textDecoration: "none" }}
                      transition="all 0.3s"
                    >
                      Learn About Agency Plans
                    </Button>
                    <Text fontSize="xs" color="whiteAlpha.600">30-day pilot &middot; No contracts</Text>
                  </VStack>
                </GridItem>
              </Grid>
            </AnimatedBox>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 11: FINAL CTA
            Strong closing call to action
            ================================================================ */}
        <Box 
          py={{ base: 12, lg: 16 }} 
          bg="linear-gradient(135deg, #0066ff 0%, #0052cc 100%)"
          position="relative"
          overflow="hidden"
        >
          <FloatingElement animation={float} delay={0} size="100px" position={{ top: "5%", left: "3%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="100px" h="100px" />
          </FloatingElement>
          <FloatingElement animation={floatReverse} delay={1.5} size="80px" position={{ bottom: "10%", right: "5%" }} opacity={0.1}>
            <Box bg="white" borderRadius="full" w="80px" h="80px" />
          </FloatingElement>

          <Container maxW="80vw" position="relative" zIndex={1}>
            <VStack spacing={6} textAlign="center">
              <AnimatedBox animation="slideUp" delay={0.2}>
                <Heading size={{ base: "xl", md: "2xl" }} color="white" lineHeight={1.2}>Ready to dominate locally?</Heading>
              </AnimatedBox>
              <AnimatedBox animation="fadeIn" delay={0.4}>
                <Text fontSize="lg" color="whiteAlpha.900" maxW="480px">
                  Join operators using Localytics to beat competitors.
                </Text>
              </AnimatedBox>
              <AnimatedBox animation="scale" delay={0.6}>
                <Flex
                  direction={{ base: "column", sm: "row" }}
                  gap={{ base: 3, sm: 4 }}
                  justify="center"
                  align={{ base: "stretch", sm: "center" }}
                  w={{ base: "full", sm: "auto" }}
                  px={{ base: 4, sm: 0 }}
                >
                  <Button
                    size="lg"
                    bg="white"
                    color="#0066FF"
                    rightIcon={<FaArrowRight />}
                    px={8}
                    fontSize="md"
                    fontWeight="bold"
                    _hover={{ transform: "translateY(-3px)", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}
                    transition="all 0.3s"
                    onClick={scrollToSignup}
                  >
                    Get My Dashboard
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    color="white"
                    borderColor="whiteAlpha.500"
                    borderWidth="2px"
                    px={6}
                    fontWeight="semibold"
                    as={RouterLink}
                    to="/login"
                    _hover={{ bg: "whiteAlpha.100", borderColor: "whiteAlpha.700", transform: "translateY(-3px)" }}
                    transition="all 0.3s"
                  >
                    Member Login
                  </Button>
                </Flex>
              </AnimatedBox>
              <AnimatedBox animation="fadeIn" delay={0.8}>
                <HStack spacing={6} pt={2} flexWrap="wrap" justify="center">
                  <HStack>
                    <Icon as={FaCheckCircle} color="green.300" />
                    <Text mb={0} color="whiteAlpha.900" fontSize="sm">Cancel anytime</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaCheckCircle} color="green.300" />
                    <Text mb={0} color="whiteAlpha.900" fontSize="sm">No contracts</Text>
                  </HStack>
                </HStack>
              </AnimatedBox>
            </VStack>
          </Container>
        </Box>


        {/* ================================================================
            SECTION 12: FOOTER
            ================================================================ */}
        <Box py={6} bg="gray.900">
          <Container maxW="90vw">
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "center", md: "center" }}
              gap={4}
            >
              <Box as={RouterLink} to="/landing" _hover={{ textDecoration: "none" }}>
                <Image src="/output.png" alt="Logo" h="36px" w="auto" objectFit="contain" cursor="pointer" />
              </Box>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  variant="link" color="gray.400" fontSize="sm" fontWeight="medium"
                  _hover={{ color: "white", textDecoration: "none" }}
                  as={RouterLink} to="/agencies"
                >
                  Agencies
                </Button>
                <Button
                  variant="link" color="gray.400" fontSize="sm" fontWeight="medium"
                  _hover={{ color: "white", textDecoration: "none" }}
                  as="a" href="https://www.instagram.com/reel/DUW8yvxDJtz/" target="_blank" rel="noopener noreferrer"
                >
                  Instagram
                </Button>
                <Button
                  variant="link" color="gray.400" fontSize="sm" fontWeight="medium"
                  _hover={{ color: "white", textDecoration: "none" }}
                  as="a" href="https://www.linkedin.com/company/localytics-ai/" target="_blank" rel="noopener noreferrer"
                >
                  LinkedIn
                </Button>
                <Button
                  variant="link" color="gray.400" fontSize="sm" fontWeight="medium"
                  _hover={{ color: "white", textDecoration: "none" }}
                  as={RouterLink} to="/login"
                >
                  Login
                </Button>
              </HStack>
              <Box textAlign={{ base: "center", md: "right" }}>
                <Text color="gray.500" fontSize="xs">
                  &copy; {new Date().getFullYear()} Localytics. All rights reserved.
                </Text>
                <Text color="gray.600" fontSize="xs" mt={1}>
                  Started, Owned and Operated in Nashville, TN.
                </Text>
              </Box>
            </Flex>
          </Container>
        </Box>

      </Box>

      {/* ================================================================
          STICKY MOBILE CTA
          Persistent bottom bar so the primary CTA is always one tap away
          after the hero scrolls out of view.
          ================================================================ */}
      <Box
        display={{ base: showStickyCta ? 'block' : 'none', md: 'none' }}
        position="fixed"
        bottom={4}
        left="50%"
        transform="translateX(-50%)"
        zIndex={1400}
        w="calc(100% - 24px)"
        maxW="360px"
        sx={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <Box position="relative">
          <Box
            position="absolute"
            inset="-2px"
            borderRadius="full"
            bg="linear-gradient(120deg, #0066FF, #22D3EE, #0066FF)"
            backgroundSize="200% 200%"
            animation={`${pulseGlow} 2.5s ease-in-out infinite, ${shimmer} 4s linear infinite`}
            opacity={0.9}
          />
          <HStack
            position="relative"
            spacing={2}
            justify="space-between"
            align="center"
            bg="rgba(8, 16, 36, 0.92)"
            backdropFilter="blur(14px)"
            border="1px solid"
            borderColor="whiteAlpha.300"
            borderRadius="full"
            px={3}
            py={2}
            boxShadow="0 12px 32px rgba(0, 0, 0, 0.35)"
          >
            <VStack align="start" spacing={0} flex="1" minW={0} pl={1}>
              <Text fontSize="xs" fontWeight="800" color="white" lineHeight={1.2} noOfLines={1}>
                Free competitive report
              </Text>
              <Text fontSize="10px" color="whiteAlpha.700" lineHeight={1.2} noOfLines={1}>
                Select GMB · 2.5 hr delivery
              </Text>
            </VStack>
            <Button
              size="sm"
              h="36px"
              bg="linear-gradient(135deg, #0066FF, #0284C7)"
              color="white"
              rightIcon={<FaArrowRight />}
              onClick={scrollToSignupForm}
              fontWeight="800"
              fontSize="xs"
              borderRadius="full"
              px={4}
              flexShrink={0}
              animation={`${pulseGlow} 2s ease-in-out infinite`}
              _hover={{ transform: 'scale(1.04)', filter: 'brightness(1.08)' }}
              _active={{ transform: 'scale(0.98)' }}
            >
              Start
            </Button>
          </HStack>
        </Box>
      </Box>
    </>
  );
};

export default ConsumerLanding;
